import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  TextField,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import fetchQueryResults, { fetchThingNames } from '../../services/query';
import ErrorAlert from '../../components/Alert/ErrorAlert';

const sensorTypes = [
  { label: 'Met Rate', value: 'MET_RATE' },
  { label: 'Heart Rate', value: 'HEART_RATE' },
  { label: 'Temp Humid', value: 'TEMP_HUMID' },
  { label: 'ECS Suit', value: 'ECS_SUIT' },
];

function DevDownloadPage() {
  const [thingNameOptions, setThingNameOptions] = useState([]);
  const [selectedThingNames, setSelectedThingNames] = useState([]);
  const [selectedSensors, setSelectedSensors] = useState([]);
  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [thingNamesWarning, setThingNamesWarning] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const names = await fetchThingNames();
        if (active) setThingNameOptions(names);
      } catch (e) {
        if (active) {
          setThingNamesWarning(
            `Could not load the thing-name list (${e.message}). You can still type thing names manually.`,
          );
        }
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const handleErrorClose = () => setError(null);

  const isValid = selectedThingNames.length > 0
    && selectedSensors.length > 0
    && startTime
    && endTime
    && startTime.isValid
    && endTime.isValid
    && endTime > startTime;

  const handleSubmit = async () => {
    if (!isValid) return;

    setIsLoading(true);
    setError(null);
    setDownloadUrl(null);

    try {
      const data = await fetchQueryResults({
        startTime,
        endTime,
        selectedSensors: selectedSensors.map((s) => s.value),
        selectedThingNames,
      });

      if (data.error === 'No data found for the specified parameters') {
        setError('No data was found for the selected parameters. Please try different search criteria.');
      } else if (data.csv_url) {
        setDownloadUrl(data.csv_url);
      }
    } catch (apiError) {
      if (apiError.response) {
        setError(`Server error: ${apiError.response.status} - ${apiError.response.data?.message || 'Unknown error'}`);
      } else if (apiError.request) {
        setError('Unable to reach the server. Please check your internet connection and try again.');
      } else {
        setError(`Error: ${apiError.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {error && <ErrorAlert message={error} onClose={handleErrorClose} />}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          padding: 4,
          maxWidth: 465,
          margin: '0 auto',
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom>
          Dev Download
        </Typography>

        {thingNamesWarning && (
          <Typography variant="body2" color="text.secondary" sx={{ width: '100%', maxWidth: 450 }}>
            {thingNamesWarning}
          </Typography>
        )}

        <Autocomplete
          multiple
          freeSolo
          options={thingNameOptions}
          value={selectedThingNames}
          onChange={(event, newValue) => setSelectedThingNames(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Thing Names"
              placeholder="Select or type thing names"
            />
          )}
          sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}
        />

        <Autocomplete
          multiple
          options={sensorTypes}
          value={selectedSensors}
          onChange={(event, newValue) => setSelectedSensors(newValue)}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Sensor Types"
              placeholder="Select sensor types"
            />
          )}
          sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}
        />

        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Box sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}>
            <DateTimePicker
              label="Start"
              value={startTime}
              onChange={setStartTime}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Box>
          <Box sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}>
            <DateTimePicker
              label="End"
              value={endTime}
              onChange={setEndTime}
              renderInput={(params) => <TextField {...params} fullWidth />}
            />
          </Box>
        </LocalizationProvider>

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !isValid}
          sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}
        >
          {isLoading ? 'Requesting Data...' : 'Request Data'}
        </Button>

        {downloadUrl && (
          <Button
            variant="outlined"
            href={downloadUrl}
            download
            sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}
          >
            Download CSV
          </Button>
        )}
      </Box>
    </>
  );
}

export default DevDownloadPage;
